"use client";

// components
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ContentColumn1,
  ContentColumn2,
  SettingsLayout,
  SettingsRow,
} from "../../settingsContentLayout";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LuCheck } from "react-icons/lu";

import ColumnMapping from "@/components/import/ColumnMapping";
import FileUploader from "@/components/import/FileUploader";
import PreviewTable from "@/components/import/PreviewTable";
import ProfileSelector from "@/components/import/ProfileSelector";

import {
  importInitialState,
  ImportProvider,
  type ImportStep,
  useImport,
} from "@/context/importContext";
import { useSession } from "@/context/SessionContext";
import { useToast } from "@/context/ToastContext";
import { useImportStatic } from "@/utils/hooks/import/importHooks";
import { loadPendingImport } from "@/utils/import/pendingImport";
import { getAvailableProfiles } from "@/utils/import/profiles/importProfiles";
import { useEffect, useRef } from "react";

interface ImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
}

// Defined outside ImportDialog: declared inside it, every change of the import
// context created a new component and remounted the step (losing scroll and focus).
const ImportDialogContent = ({ onClose }: { onClose: () => void }) => {
  const { importData } = useImport();
  const { totalRecords, selectedProfile, currentStep } = importData;
  const steps: ImportStep[] = ["profile", "upload", "columns", "preview", "complete"];
  const currentIndex = steps.indexOf(currentStep);

  const getStepTitle = () => {
    switch (currentStep) {
      case "profile":
        return "Selecciona un perfil de importación para comenzar";
      case "upload":
        return "Pega los datos o sube un archivo";
      case "columns":
        return "Asigna las columnas";
      case "preview":
        return "Validación de datos";
      case "complete":
        return "Importación completada exitosamente";
      default:
        return "";
    }
  };

  return (
    <div className="w-full overflow-x-auto space-y-6">
      {/* Header - Solo mostrar si no estamos en la pantalla de resultados */}
      {currentStep !== "preview" && (
        <div className="flex items-center justify-between">
          <p className="text-xl font-bold">{getStepTitle()}</p>

          {/* Progress Steps */}
          <div className="flex items-center justify-end gap-2">
            {steps.map((step, index) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentIndex >= index ? "bg-ac text-white" : "bg-bg+2 text-fg-muted"
                  }`}
                >
                  {currentIndex > index ? <LuCheck className="h-4 w-4" /> : index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`w-8 h-0.5 ${currentIndex > index ? "bg-ac" : "bg-bg+2"}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      {currentStep === "profile" && <ProfileSelector />}

      {currentStep === "upload" && selectedProfile && <FileUploader />}

      {currentStep === "columns" && selectedProfile && <ColumnMapping />}

      {currentStep === "preview" && (
        <div className="space-y-6">
          <PreviewTable />
        </div>
      )}

      {currentStep === "complete" && (
        <Card className="border-su-2 bg-su-3">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-su rounded-full flex items-center justify-center mb-4">
              <LuCheck className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-su">
              ¡Importación Completada!
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-fg-muted">
              Se importaron exitosamente {totalRecords} registros usando el
              perfil {selectedProfile?.name.es}.
            </p>
            <Button onClick={onClose} className="bg-ac hover:bg-ac+1">
              Continuar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};


const ImportDialog = ({
  isOpen,
  onClose,
  title = "Importar datos",
  description = "",
}: ImportDialogProps) => {
  // Obtener el currentStep para determinar si mostrar el header
  const { importData } = useImport();
  const { currentStep } = importData;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-6xl"
        closeButton={currentStep !== "preview"}
        aria-describedby={undefined}
      >
        {/* Solo mostrar el header si no estamos en la pantalla de resultados */}
        {currentStep !== "preview" && (
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-4">
              {title}
            </DialogTitle>
            <p className="text-gray-600 mt-2">{description}</p>
          </DialogHeader>
        )}
        <ImportDialogContent onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
};

/**
 * "Continuar importación": the answer of Orquesti to a limit request links here with
 * ?resumeImport=1. The import that waited in this browser (never in Orquesti) opens at
 * the matching of its columns, ready to be imported again.
 */
const useResumePendingImport = () => {
  const { setImportData } = useImport();
  const { session } = useSession();
  const { showToast } = useToast();
  const staticData = useImportStatic(session.languageCode);
  // The profiles need the static data, which is a new object on every render.
  const staticDataRef = useRef(staticData);
  staticDataRef.current = staticData;
  const isLoading = staticData.isLoading;
  const companyId = session.companyId;

  useEffect(() => {
    if (typeof window === "undefined" || isLoading) return;
    if (new URLSearchParams(window.location.search).get("resumeImport") !== "1") return;

    // The flag goes away so a reload does not reopen the dialog.
    const url = new URL(window.location.href);
    url.searchParams.delete("resumeImport");
    window.history.replaceState(null, "", url.toString());

    void loadPendingImport(companyId).then((pending) => {
      const profile = pending
        ? getAvailableProfiles(staticDataRef.current).find((candidate) => candidate.id === pending.profileId)
        : undefined;
      if (!pending || !profile) {
        showToast({
          description:
            "No encontramos la importación guardada en este navegador. Vuelve a cargar tu archivo.",
          variant: "destructive",
        });
        return;
      }
      setImportData({
        ...importInitialState,
        open: true,
        selectedProfile: profile,
        importType: profile.id,
        source: pending.source,
        currentStep: "columns",
      });
    });
  }, [isLoading, companyId, setImportData, showToast]);
};

const Import = () => {
  const ImportDialogWrapper = () => {
    const { importData, setImportData } = useImport();
    useResumePendingImport();

    return (
      <Dialog
        open={importData.open}
        onOpenChange={() =>
          setImportData(() => ({
            ...importInitialState,
            open: true,
          }))
        }
      >
        <DialogTrigger asChild>
          <div className="flex items-center justify-end">
            <Button>Importar</Button>
          </div>
        </DialogTrigger>
        <ImportDialog
          isOpen={importData.open}
          onClose={() =>
            setImportData(() => ({
              ...importInitialState,
              open: false,
            }))
          }
          title="Importar Datos"
          description=""
        />
      </Dialog>
    );
  };

  return (
    <ImportProvider>
      <SettingsLayout header="Importar datos">
        <SettingsRow>
          <ContentColumn1 description="Pega datos o sube un archivo de Excel o CSV" />
          <ContentColumn2 size="full">
            <ImportDialogWrapper />
          </ContentColumn2>
        </SettingsRow>
      </SettingsLayout>
    </ImportProvider>
  );
};

export default Import;
export { ImportDialog };
